import { useCallback, useMemo, useState } from 'react';

import { useIntl } from 'react-intl';

import { ILocation } from '@rbi-ctg/frontend';
import { IStore } from '@rbi-ctg/store';
import { RbiOrderStatus, useGetUniqueStoresQuery } from 'generated/rbi-graphql';
import useAuth from 'hooks/auth/use-auth';
import { useFavoriteStores } from 'hooks/favorite-stores';
import useDialogModal from 'hooks/use-dialog-modal';
import useErrorModal from 'hooks/use-error-modal';
import {
  StoreAction,
  searchFavRequested,
  searchFavsError,
  searchFavsSuccess,
  searchNearbyError,
  searchNearbyRequested,
  searchNearbySuccess,
  searchRecentError,
  searchRecentRequested,
  searchRecentSuccess
} from 'pages/store-locator/ducks/stores';
import {
  getAllRestaurantsByIds,
  getAllRestaurantsByIdsOrderedByLocation,
  getAllRestaurantsByStoreNumbers,
  getAllRestaurantsByStoreNumbersOrderedByLocation,
  getRestaurantsNearMe
} from 'remote/api/restaurants';
import { useNetworkContext } from 'state/network';
import { useServiceModeContext } from 'state/service-mode';
import { isValidPosition } from 'utils/geolocation';
import { mergeRestaurantData, useGetRestaurantFn } from 'utils/restaurant';
import { isCatering } from 'utils/service-mode';

interface IBounds {
  ne: ILocation;
  sw: ILocation;
}
interface ISearchStoresProps {
  neLat: number;
  neLng: number;
  swLat: number;
  swLng: number;
  center: ILocation;
  enableQueryRecentStores?: boolean;
  storeLocatorDispatch: StoreAction;
}
export default function useSearchStores({
  neLat,
  neLng,
  swLat,
  swLng,
  center,
  enableQueryRecentStores,
  storeLocatorDispatch
}: ISearchStoresProps) {
  const { connection, sanityEndpoints } = useNetworkContext();
  const [activeStoreId, setActiveStoreId] = useState('');
  const { favoriteStoreIds } = useFavoriteStores();
  const { isAuthenticated } = useAuth();
  const getRbiRestaurant = useGetRestaurantFn();
  const { serviceMode } = useServiceModeContext();

  const { data: uniqueStoreOrders, loading: uniqueStoreOrdersLoading } = useGetUniqueStoresQuery({
    skip: !enableQueryRecentStores || !isAuthenticated(),
    variables: {
      orderStatuses: [
        RbiOrderStatus.CATERING_SUCCESSFUL,
        RbiOrderStatus.CATERING_PLACED,
        RbiOrderStatus.INSERT_SUCCESSFUL,
        RbiOrderStatus.UPDATE_SUCCESSFUL
      ]
    },
    fetchPolicy: 'cache-and-network'
  });

  const recentStoreIds = useMemo(() => {
    const uniqueStores = uniqueStoreOrders?.uniqueStores.orders?.map(order => order?.cart.storeId);
    return uniqueStores?.filter((id: string | undefined): id is string => !!id);
  }, [uniqueStoreOrders]);
  const [ErrorDialog, openErrorDialog] = useErrorModal({
    modalAppearanceEventMessage: 'Error: Fetching Restaurants Error'
  });
  const [Dialog, openDialog] = useDialogModal({
    modalAppearanceEventMessage: 'Notify user to enable location services'
  });
  const { formatMessage } = useIntl();
  /**
   * Not all fields are sourced directly from sanity. For specific fields such as operating hours
   * and holiday hours, we must query our gql layer for
   *
   * This function takes a collection of sanity stores, and merges the relevant RbiRestaurant
   * data for that particular store
   */
  const parseSanityStores = useCallback(
    async (sanityStores: IStore[]): Promise<IStore[]> => {
      // this is only a temporary measure until we move the entire store query to the gql layer
      // ideally we'd have a way to request a collection of IRbiRestaurants to merge
      // however that is currently not supported.
      const parseStoreRequests = sanityStores.map(
        async (sanityStore): Promise<IStore> => {
          const rbiRestaurant = await getRbiRestaurant(sanityStore.number ?? '');

          return mergeRestaurantData({ rbiRestaurant, sanityStore });
        }
      );

      const parsedStores = await Promise.all(parseStoreRequests);
      return parsedStores;
    },
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const searchNearbyRestaurants = useCallback(
    async (location: ILocation, bounds?: IBounds | null, deliveryOnly: boolean = false) => {
      storeLocatorDispatch(searchNearbyRequested());
      // location could be an empty object, so verify it has at least a lat
      // similar check happening in use-store-locator
      if (!location || (location && !location.lat)) {
        location = center;
      }
      const boundingBox = bounds
        ? {
            minLat: bounds.sw.lat,
            maxLat: bounds.ne.lat,
            minLng: bounds.sw.lng,
            maxLng: bounds.ne.lng
          }
        : { minLat: swLat, maxLat: neLat, minLng: swLng, maxLng: neLng };
      if (!isValidPosition(location)) {
        openDialog({
          message: formatMessage({ id: 'messageAllowLocation' }),
          title: formatMessage({ id: 'titleAllowLocation' })
        });
        return Promise.resolve();
      }
      try {
        const sanityStores: IStore[] = await getRestaurantsNearMe(sanityEndpoints.groq, {
          ...boundingBox,
          connection,
          userLat: location.lat,
          userLng: location.lng,
          deliveryOnly
        });
        const parsedStores = await parseSanityStores(sanityStores);
        const results = searchNearbySuccess(parsedStores);
        return storeLocatorDispatch(results);
      } catch (error) {
        storeLocatorDispatch(searchNearbyError(error));
        openErrorDialog({ message: 'Could not retrieve restaurants', error });
      }
    },
    // eslint-disable-next-line
    [
      storeLocatorDispatch,
      sanityEndpoints.groq,
      connection,
      openDialog,
      formatMessage,
      openErrorDialog
    ]
  );
  const searchFavRestaurants = useCallback(
    async (location: ILocation | null) => {
      storeLocatorDispatch(searchFavRequested());
      try {
        // NOTE: location could be empty object, so just verify it has at least "lat"
        // similar check happening in use-store-locator
        const sanityStores: IStore[] = await (location?.lat
          ? getAllRestaurantsByIdsOrderedByLocation(sanityEndpoints.groq, {
              connection,
              storeIds: favoriteStoreIds,
              userLat: location.lat,
              userLng: location.lng
            })
          : getAllRestaurantsByIds(sanityEndpoints.groq, {
              connection,
              storeIds: favoriteStoreIds
            }));
        const parsedStores = await parseSanityStores(sanityStores);
        return storeLocatorDispatch(searchFavsSuccess(parsedStores));
      } catch (error) {
        storeLocatorDispatch(searchFavsError(error));
        openErrorDialog({ message: 'Could not retrieve favorite restaurants', error });
      }
    },
    [
      connection,
      favoriteStoreIds,
      openErrorDialog,
      parseSanityStores,
      sanityEndpoints.groq,
      storeLocatorDispatch
    ]
  );
  const searchRecentRestaurants = useCallback(
    async (location: ILocation | null) => {
      if (!isAuthenticated()) {
        storeLocatorDispatch(searchRecentSuccess([]));
        return;
      }
      storeLocatorDispatch(searchRecentRequested());
      // If the user has no recentStoreIds and the uniqueStoreOrders query is not loading
      // anymore, set the user recent list to an empty one
      if (!recentStoreIds) {
        if (!uniqueStoreOrdersLoading) {
          storeLocatorDispatch(searchRecentSuccess([]));
          return;
        }
        return;
      }
      try {
        const sanityStores: IStore[] = (
          await (location?.lat
            ? getAllRestaurantsByStoreNumbersOrderedByLocation(sanityEndpoints.groq, {
                connection,
                storeNumbers: recentStoreIds,
                userLat: location.lat,
                userLng: location.lng
              })
            : getAllRestaurantsByStoreNumbers(sanityEndpoints.groq, {
                connection,
                storeNumbers: recentStoreIds
              }))
        )
          // If the service mode selected is catering and the store has no catering service available, filter it out of the list
          .filter((store: IStore) => !isCatering(serviceMode) || store.hasCatering);

        const parsedStores = await parseSanityStores(sanityStores);
        return storeLocatorDispatch(searchRecentSuccess(parsedStores));
      } catch (error) {
        storeLocatorDispatch(searchRecentError(error));
        openErrorDialog({ message: 'Could not retrieve recent restaurants', error });
      }
    },
    [
      connection,
      isAuthenticated,
      openErrorDialog,
      parseSanityStores,
      recentStoreIds,
      sanityEndpoints.groq,
      serviceMode,
      storeLocatorDispatch,
      uniqueStoreOrdersLoading
    ]
  );
  return {
    activeStoreId,
    ErrorDialog,
    Dialog,
    searchNearbyRestaurants,
    setActiveStoreId,
    searchFavRestaurants,
    searchRecentRestaurants
  };
}
